import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { formatMoneyLegacy } from '../../utils/currency';
import { getGroupTours, showGroupTour, approveGroupTour, rejectGroupTour } from '../../api/groupToursApi';
import { getSharedRentals, showSharedRental, approveSharedRental, rejectSharedRental } from '../../api/sharedRentalsApi';
import { getPackages, showPackage, approvePackage, rejectPackage } from '../../api/packageApi';
import { getVendorListingsSummary } from '../../api/adminApi';
import swal from 'sweetalert';

const TAB_GROUP = 'group_tours';
const TAB_SHARED = 'shared_rentals';
const TAB_PACKAGES = 'packages';

const FILTER_ALL = 'all';
const FILTER_PENDING = 'pending';
const FILTER_LIVE = 'approved';
const FILTER_REJECTED = 'rejected';

function assignmentTypesLabel(partner) {
	const at = partner?.assignment_types;
	if (!at || !at.trim()) return '—';
	const list = at.split(',').map((s) => s.trim()).filter(Boolean);
	const labels = { GroupTour: 'Group', SharedRental: 'Shared', PartnerService: 'Private' };
	return list.map((t) => labels[t] || t).join(', ');
}

export default function VendorListingsTabs({ vendorId, partner, onRefresh }) {
	const [summary, setSummary] = useState({ group_tours: {}, shared_rentals: {}, packages: {} });
	const [activeTab, setActiveTab] = useState(TAB_GROUP);
	const [filter, setFilter] = useState(FILTER_ALL);
	const [list, setList] = useState([]);
	const [loading, setLoading] = useState(false);
	const [actionId, setActionId] = useState(null);

	const vid = partner?.id ?? partner?.idx;

	const fetchSummary = useCallback(() => {
		if (!vendorId) return;
		getVendorListingsSummary(vendorId)
			.then((res) => setSummary(res.data || {}))
			.catch(() => {});
	}, [vendorId]);

	useEffect(() => {
		fetchSummary();
	}, [fetchSummary]);

	useEffect(() => {
		if (!vid) return;
		setLoading(true);
		const adminParams = { admin_list: 'true', vendor_id: vid };
		if (filter !== FILTER_ALL) adminParams.approval_status = filter;

		const thenSet = (raw, key) => {
			const arr = Array.isArray(raw) ? raw : (raw?.[key] || raw?.data || []);
			setList(Array.isArray(arr) ? arr.filter((t) => t && (t.is_deleted !== true)) : []);
		};

		if (activeTab === TAB_GROUP) {
			getGroupTours(adminParams)
				.then((r) => thenSet(r.data, 'group_tours'))
				.catch(() => setList([]))
				.finally(() => setLoading(false));
		} else if (activeTab === TAB_SHARED) {
			getSharedRentals(adminParams)
				.then((r) => thenSet(r.data, 'shared_rentals'))
				.catch(() => setList([]))
				.finally(() => setLoading(false));
		} else {
			getPackages({ ...adminParams, vendor_id: vid })
				.then((r) => thenSet(r.data, 'packages'))
				.catch(() => setList([]))
				.finally(() => setLoading(false));
		}
	}, [vid, activeTab, filter]);

	const handleApprove = (item) => {
		const id = item.idx || item.id;
		const name = item.name || item.title;
		setActionId(id);
		const approveFn = activeTab === TAB_GROUP ? approveGroupTour : activeTab === TAB_SHARED ? approveSharedRental : approvePackage;
		approveFn(id)
			.then(() => {
				swal('Approved', `"${name}" is now live.`, 'success');
				fetchSummary();
				onRefresh?.();
				setList((prev) => prev.filter((t) => (t.idx || t.id) !== id));
				window.dispatchEvent(new CustomEvent('admin:refresh-pending-count'));
			})
			.catch((e) => swal('Error', (e.response?.data?.errors || []).join(' ') || 'Failed', 'error'))
			.finally(() => setActionId(null));
	};

	const handleReject = (item) => {
		const id = item.idx || item.id;
		const name = item.name || item.title;
		swal({ title: 'Reject listing', text: 'Enter reason (required):', content: { element: 'input', attributes: { type: 'text', placeholder: 'Reason for rejection' } }, buttons: { cancel: true, confirm: 'Reject' } })
			.then((reason) => {
				if (reason == null) return;
				const trimmed = (reason || '').trim();
				if (!trimmed) {
					swal('Required', 'Rejection reason is required.', 'warning');
					return;
				}
				setActionId(id);
				const rejectFn = activeTab === TAB_GROUP ? rejectGroupTour : activeTab === TAB_SHARED ? rejectSharedRental : rejectPackage;
				rejectFn(id, trimmed)
					.then(() => {
						swal('Rejected', `"${name}" has been rejected.`, 'success');
						fetchSummary();
						onRefresh?.();
						setList((prev) => prev.map((t) => ((t.idx || t.id) === id ? { ...t, approval_status: 'rejected', rejection_reason: trimmed } : t)));
						window.dispatchEvent(new CustomEvent('admin:refresh-pending-count'));
					})
					.catch((e) => swal('Error', (e.response?.data?.errors || []).join(' ') || 'Failed', 'error'))
					.finally(() => setActionId(null));
			});
	};

	const gt = summary.group_tours || {};
	const sr = summary.shared_rentals || {};
	const pkg = summary.packages || {};
	const pendingTotal = (gt.pending || 0) + (sr.pending || 0) + (pkg.pending || 0);
	const liveTotal = (gt.live || 0) + (sr.live || 0) + (pkg.live || 0);

	return (
		<div className="vendor-listings-moderation mt-4">
			<div className="card">
				<div className="card-body">
					<h5 className="card-title">Vendor listings (approval)</h5>
					<div className="d-flex flex-wrap align-items-center mb-3">
						<span className="mr-3">
							<span className="text-danger font-weight-bold">Pending:</span> G{gt.pending || 0} · S{sr.pending || 0} · P{pkg.pending || 0}
						</span>
						<span>
							<span className="text-success font-weight-bold">Live:</span> {liveTotal} approved
						</span>
					</div>
					<ul className="nav nav-tabs">
						<li className="nav-item">
							<button type="button" className={`nav-link ${activeTab === TAB_GROUP ? 'active' : ''}`} onClick={() => setActiveTab(TAB_GROUP)}>
								Group Trips ({gt.pending || 0} pending / {gt.live || 0} live)
							</button>
						</li>
						<li className="nav-item">
							<button type="button" className={`nav-link ${activeTab === TAB_SHARED ? 'active' : ''}`} onClick={() => setActiveTab(TAB_SHARED)}>
								Shared Rentals ({sr.pending || 0} pending / {sr.live || 0} live)
							</button>
						</li>
						<li className="nav-item">
							<button type="button" className={`nav-link ${activeTab === TAB_PACKAGES ? 'active' : ''}`} onClick={() => setActiveTab(TAB_PACKAGES)}>
								Private Tours ({pkg.pending || 0} pending / {pkg.live || 0} live)
							</button>
						</li>
					</ul>
					<div className="mt-3">
						<div className="btn-group btn-group-sm mb-2">
							{[FILTER_ALL, FILTER_PENDING, FILTER_LIVE, FILTER_REJECTED].map((f) => (
								<button key={f} type="button" className={`btn ${filter === f ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setFilter(f)}>
									{f === FILTER_ALL ? 'All' : f === FILTER_PENDING ? 'Pending Approval' : f === FILTER_LIVE ? 'Live' : 'Rejected'}
								</button>
							))}
						</div>
						{loading ? (
							<p className="text-muted">Loading…</p>
						) : list.length === 0 ? (
							<p className="text-muted">No listings in this filter.</p>
						) : (
							<div className="table-responsive">
								<table className="table table-bordered table-sm">
									<thead>
										<tr>
											<th>Title</th>
											<th>Date</th>
											{activeTab === TAB_GROUP ? (
												<>
													<th title="Vendor base price per person">Vendor Base</th>
													<th title="Markup percentage">Markup %</th>
													<th title="Final customer price per person">Final Price</th>
													<th title="Interest percentage">Interest %</th>
													<th title="Interest amount per person">Interest Amt</th>
													<th title="Remaining payment per person">Remaining</th>
													<th title="Platform margin per person">Platform</th>
													<th title="Vendor payout per person">Vendor Payout</th>
												</>
											) : activeTab === TAB_PACKAGES ? (
												<>
													<th title="Vendor price">Vendor</th>
													<th title="Final price">Final</th>
												</>
											) : (
												<>
													<th title="Vendor price">Vendor</th>
													<th title="Interest">Interest</th>
													<th title="Final price">Final</th>
												</>
											)}
											<th>Submitted</th>
											<th>Status</th>
											<th>Actions</th>
										</tr>
									</thead>
									<tbody>
										{list.map((row) => {
											const id = row.idx || row.id;
											const status = row.approval_status || 'pending';
											const isPending = status === 'pending';
											const isRejected = status === 'rejected';
											const dateStr = row.start_date ? moment(row.start_date).format('DD MMM YYYY') : (row.preferred_dates || '—');
											const fmt = (x) => (x != null ? Number(x).toLocaleString() : '—');
											const pct = (x) => (x != null ? `${Number(x)}%` : '—');
											const cc = row.currency_code || 'NPR';
											const money = (x) => (x != null && x !== '' ? formatMoneyLegacy(x, cc) : '—');
											const submitted = row.created_at ? moment(row.created_at).format('DD MMM YYYY') : '—';
											return (
												<tr key={id}>
													<td>{row.name || row.title || '—'}</td>
													<td>{dateStr}</td>
													{activeTab === TAB_GROUP ? (
														<>
															<td>{money(row.vendor_total_price)}</td>
															<td>{pct(row.markup_percentage)}</td>
															<td>{money(row.final_price)}</td>
															<td>{pct(row.interest_percentage)}</td>
															<td>{money(row.interest_amount)}</td>
															<td>{money(row.remaining_payment)}</td>
															<td>{money(row.platform_margin)}</td>
															<td>{money(row.vendor_payout)}</td>
														</>
													) : activeTab === TAB_PACKAGES ? (
														<>
															<td>{money(row.vendor_total_price)}</td>
															<td>{money(row.final_price)}</td>
														</>
													) : (
														<>
															<td>{money(row.vendor_total_price)}</td>
															<td>{money(row.interest_amount)}</td>
															<td>{money(row.final_price)}</td>
														</>
													)}
													<td>{submitted}</td>
													<td>
														<span className={`badge badge-${isPending ? 'warning' : isRejected ? 'danger' : 'success'}`}>{status}</span>
														{isRejected && row.rejection_reason && <small className="d-block text-muted">{row.rejection_reason}</small>}
													</td>
													<td>
														{isPending && (
															<>
																<button type="button" className="btn btn-success btn-sm mr-1" disabled={actionId === id} onClick={() => handleApprove(row)}>
																	Approve
																</button>
																<button type="button" className="btn btn-danger btn-sm" disabled={actionId === id} onClick={() => handleReject(row)}>
																	Reject
																</button>
															</>
														)}
														{activeTab === TAB_GROUP && (isPending || status === 'approved') && (
															<Link to={`/admin/group_tour_details/${id}`} className="btn btn-outline-secondary btn-sm ml-1">
																Manage
															</Link>
														)}
														{activeTab === TAB_SHARED && (isPending || status === 'approved') && (
															<Link to={`/admin/shared_rental_details/${id}`} className="btn btn-outline-secondary btn-sm ml-1">
																Manage
															</Link>
														)}
														{activeTab === TAB_PACKAGES && (isPending || status === 'approved') && (
															<Link to={`/admin/package_details/${id}`} className="btn btn-outline-secondary btn-sm ml-1">
																Manage
															</Link>
														)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export { assignmentTypesLabel };
